import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  Save,
  Play,
  RefreshCw,
  Bell,
  ChevronRight
} from 'lucide-react';

const SEVERITY_OPTIONS = [
  { value: 'CRITICA', label: 'Crítica', color: '#ef4444' },
  { value: 'ALTA', label: 'Alta', color: '#f97316' },
  { value: 'MEDIA', label: 'Media', color: '#eab308' },
  { value: 'BAJA', label: 'Baja', color: '#22c55e' }
];

export const EscalationConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState({
    horas_para_escalar: 24,
    severidades: ['ALTA', 'CRITICA'],
    activo: true
  });
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [lastEscalationResult, setLastEscalationResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, pendingRes] = await Promise.all([
        api.get('/escalation/config'),
        api.get('/escalation/pending')
      ]);
      
      setConfig(configRes.data);
      setPendingAlerts(pendingRes.data.alertas || []);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/escalation/config', config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const response = await api.post('/escalation/run');
      setLastEscalationResult(response.data);
      toast.success(`${response.data.escaladas} alertas escaladas`);
      loadData(); // Recargar datos
    } catch (error) {
      toast.error('Error al ejecutar escalamiento');
    } finally {
      setRunning(false);
    }
  };

  const toggleSeverity = (severity) => {
    const current = config.severidades || [];
    const newSeverities = current.includes(severity)
      ? current.filter(s => s !== severity)
      : [...current, severity];
    setConfig({ ...config, severidades: newSeverities });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="escalation-config-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Escalamiento Automático
          </h1>
          <p className="text-slate-600 mt-1">
            Configuración de escalamiento de alertas no atendidas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            onClick={handleRun}
            disabled={running}
            variant="outline"
            style={{ borderColor: '#f97316', color: '#f97316' }}
            data-testid="run-escalation-btn"
          >
            {running ? (
              <RefreshCw size={16} className="mr-2 animate-spin" />
            ) : (
              <Play size={16} className="mr-2" />
            )}
            Ejecutar Ahora
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            data-testid="save-config-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuración */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 bg-white border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-blue-600" />
              Configuración de Escalamiento
            </h3>
            
            <div className="space-y-6">
              {/* Activo */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                <div>
                  <Label className="text-slate-900 font-medium">Escalamiento Activo</Label>
                  <p className="text-sm text-slate-500">Habilitar o deshabilitar el escalamiento automático</p>
                </div>
                <Switch
                  checked={config.activo}
                  onCheckedChange={(checked) => setConfig({ ...config, activo: checked })}
                />
              </div>

              {/* Horas */}
              <div>
                <Label className="text-slate-700 mb-2 block">
                  Horas para Escalar
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={config.horas_para_escalar}
                    onChange={(e) => setConfig({ ...config, horas_para_escalar: parseInt(e.target.value) || 24 })}
                    className="w-32"
                    style={{ backgroundColor: '#ffffff' }}
                  />
                  <span className="text-slate-600">horas sin atención</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Las alertas que permanezcan en estado "NUEVA" por más de este tiempo serán escaladas automáticamente
                </p>
              </div>

              {/* Severidades */}
              <div>
                <Label className="text-slate-700 mb-2 block">
                  Severidades a Escalar
                </Label>
                <p className="text-xs text-slate-500 mb-3">
                  Selecciona qué severidades serán consideradas para escalamiento
                </p>
                <div className="flex flex-wrap gap-3">
                  {SEVERITY_OPTIONS.map(sev => (
                    <button
                      key={sev.value}
                      onClick={() => toggleSeverity(sev.value)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        config.severidades?.includes(sev.value)
                          ? 'border-current'
                          : 'border-slate-200 opacity-50'
                      }`}
                      style={{ 
                        borderColor: config.severidades?.includes(sev.value) ? sev.color : undefined,
                        backgroundColor: config.severidades?.includes(sev.value) ? `${sev.color}15` : undefined
                      }}
                    >
                      <span 
                        className="font-medium"
                        style={{ color: sev.color }}
                      >
                        {sev.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Último resultado */}
          {lastEscalationResult && (
            <Card className="p-6 bg-white border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                Último Escalamiento Ejecutado
              </h3>
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-green-800 font-medium">
                  {lastEscalationResult.escaladas} alertas escaladas
                </p>
                {lastEscalationResult.alertas?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {lastEscalationResult.alertas.slice(0, 5).map(a => (
                      <li key={a.id} className="text-sm text-green-700">
                        • {a.titulo} ({a.severidad}) - {a.horas_pendiente}h pendiente
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Alertas próximas a escalar */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Bell size={20} className="text-orange-600" />
            Próximas a Escalar
          </h3>
          
          {pendingAlerts.length > 0 ? (
            <div className="space-y-3">
              {pendingAlerts.map(alert => (
                <div 
                  key={alert.id}
                  className="p-3 rounded-lg border border-orange-200 bg-orange-50"
                >
                  <p className="font-medium text-slate-900 text-sm line-clamp-1">
                    {alert.titulo}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">
                      {alert.horas_transcurridas}h transcurridas
                    </span>
                    <span className="text-xs font-medium text-orange-600">
                      {alert.horas_restantes}h restantes
                    </span>
                  </div>
                  <div className="mt-2 w-full h-1.5 rounded-full bg-slate-200">
                    <div 
                      className="h-full rounded-full bg-orange-500"
                      style={{ width: `${alert.porcentaje_tiempo}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <AlertTriangle size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay alertas próximas a escalar</p>
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-4">
            Alertas que han consumido más del 75% del tiempo límite antes de ser escaladas
          </p>
        </Card>
      </div>
    </div>
  );
};

export default EscalationConfig;
